import FilterDate from '@/Enums/FilterDate.enum';
import Dimension from '@/Enums/ReportBuilder/Dimension.enum';
import Metric from '@/Enums/ReportBuilder/Metric.enum';
import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import TimeBucket from '@/Enums/ReportBuilder/TimeBucket.enum';
import type { IClientControlOption, IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import type { IFilterFormData } from '@/Interfaces/ReportBuilder/FilterFormData.interface';
import type { ICustomReportTemplateOption } from '@/Interfaces/ReportBuilder/Template.interface';
import ClearAllButton from '@/components/Buttons/ClearAllButton';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import FiltersBadge from '@/components/Filter/Badge';
import FormErrorHelperText from '@/components/Form/FormErrorHelperText';
import SearchListIcon from '@/components/Icons/SearchListIcon';
import SearchListIconAlt from '@/components/Icons/SearchListIconAlt';
import TextFirstlineLeftIcon from '@/components/Icons/TextFirstlineLeftIcon';
import TvIcon from '@/components/Icons/TvIcon';
import ColumnSelect from '@/components/Select/ColumnSelect';
import SelectInputPlaceholder from '@/components/Select/InputPlaceholder';
import { TIME_LABELS } from '@/constants/reportBuilder';
import {
  attributionDimensions,
  pacingCompatibleDimensions,
  pacingIncompatibleMetrics,
  pacingMetrics,
  performanceOnlyDimensions,
} from '@/constants/reportBuilder/beeswax';
import { PLATFORMS_OPTIONS } from '@/constants/reportBuilder/omniPlatform';
import { getShortcutItemsInclusive } from '@/helpers/datePicker/shortcuts';
import { getDefaultFilters } from '@/helpers/reportBuilder';
import type { GetOptionDisabled } from '@/types/Controls/TabSelect.types';
import type { FilterSourceLoader } from '@/types/ReportBuilder/FilterPopover.types';
import { AccessTime, ArrowDropDown, SaveOutlined, TableRowsOutlined, Web } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  MenuItem,
  Paper,
  Select,
  TextField,
  autocompleteClasses,
  inputBaseClasses,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import type { Moment } from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, type SubmitHandler, useFormContext } from 'react-hook-form';
import FilterPopover from './FilterPopover';
import ScheduleReportTooltip from './ScheduleReportTooltip';

interface IProps {
  clients: IClientControlOption[];
  loadingClients: boolean;
  campaigns: IControlOption[];
  events?: IControlOption[];
  loadCampaigns: FilterSourceLoader;
  loadEvents?: FilterSourceLoader;
  loadingCampaigns: boolean;
  loadingEvents?: boolean;
  templates: ICustomReportTemplateOption[];
  loadingTemplates: boolean;
  onSubmit: (formData: IFilterFormData) => Promise<void>;
  onScheduleReportClick: () => void;
  onSaveAsTemplateClick: () => void;
  loadTemplateFilters: (id: number | undefined) => Promise<void>;
  usePlatform?: boolean;
  checkColumnExclusion?: boolean;
  dimensionOptions: IGroupedControlOption<string, Dimension>[];
  metricOptions: IControlOption<string, Metric>[];
  groupedMetrics?: boolean;
  showEventFilter?: boolean;
}

const Filter = ({
  clients,
  loadingClients,
  campaigns,
  events,
  loadCampaigns,
  loadEvents,
  loadingCampaigns,
  loadingEvents,
  templates,
  loadingTemplates,
  onSubmit,
  onScheduleReportClick,
  onSaveAsTemplateClick,
  loadTemplateFilters,
  usePlatform,
  checkColumnExclusion,
  dimensionOptions,
  metricOptions,
  groupedMetrics,
  showEventFilter,
}: IProps) => {
  const { watch, control, reset, setValue, handleSubmit, clearErrors, formState } = useFormContext<IFilterFormData>();

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (
        ['metrics', 'dimensions'].includes(name!) &&
        type === 'change' &&
        (value.metrics?.length || value.dimensions?.length)
      ) {
        clearErrors('dimensions');
        clearErrors('metrics');
        return;
      }
      if (name === 'template') {
        loadTemplateFilters(value.template?.id);
      }
    });
    return () => subscription.unsubscribe();
  }, [campaigns, clearErrors, clients, loadTemplateFilters, setValue, watch]);

  const [
    timeBucket,
    platforms,
    dateRange,
    dateRangeFormat,
    filterClients,
    filterAdvertisers,
    filterCampaigns,
    filterEvents,
    dimensions,
    metrics,
  ] = watch([
    'timeBucket',
    'platforms',
    'dateRange',
    'dateRangeFormat',
    'clients',
    'advertisers',
    'campaigns',
    'events',
    'dimensions',
    'metrics',
  ]);

  const filterCount = useMemo(
    () =>
      [
        timeBucket ? 1 : 0,
        platforms ? 1 : 0,
        dateRange?.length ? 1 : 0,
        filterClients?.length || 0,
        filterAdvertisers?.length || 0,
        filterCampaigns?.length || 0,
        filterEvents?.length || 0,
        dimensions?.length || 0,
        metrics?.length || 0,
      ].reduce((sum, curr) => sum + curr, 0),
    [
      filterAdvertisers?.length,
      filterCampaigns?.length,
      filterEvents?.length,
      dateRange?.length,
      platforms?.length,
      dimensions?.length,
      filterClients?.length,
      metrics?.length,
      timeBucket,
    ],
  );

  const onDateRangeChange = useCallback(
    (value: DateRange<Moment>, format?: FilterDate) => {
      setValue('dateRange', value);
      if (format) {
        setValue('dateRangeFormat', format);
      }
    },
    [setValue],
  );

  const onReset = useCallback(() => {
    reset(getDefaultFilters());
  }, [reset]);

  const onFormSubmit: SubmitHandler<IFilterFormData> = formData => {
    onSubmit?.(formData);
  };

  const omniPlatformDisabledCondition = useMemo(
    () => usePlatform && !platforms?.length,
    [usePlatform, platforms?.length],
  );
  const apiActionsDisabled = useMemo(
    () => !formState.isValid || omniPlatformDisabledCondition,
    [formState.isValid, omniPlatformDisabledCondition],
  );
  const scheduleReportsDisabled = useMemo(
    () => apiActionsDisabled || dateRangeFormat === FilterDate.CUSTOM_RANGE,
    [apiActionsDisabled, dateRangeFormat],
  );

  const checkShouldStopDisabledCheckEarly = useCallback(
    () =>
      !checkColumnExclusion ||
      (usePlatform && !platforms?.some(platform => platform.value === CustomReportChannel.Beeswax)),
    [platforms, usePlatform, checkColumnExclusion],
  );

  const getDimensionDisabled = useCallback<GetOptionDisabled<IControlOption<string, Dimension>>>(
    (option, value, checked) => {
      if (checked || checkShouldStopDisabledCheckEarly()) {
        return false;
      }
      const dimension = option.value;
      // When a pacing metric is selected, only Line Item / Campaign dimensions are valid.
      if (
        !pacingCompatibleDimensions.includes(dimension) &&
        (metrics ?? []).some(selectedMetric => pacingMetrics.includes(selectedMetric.value))
      ) {
        return true;
      }
      if (
        attributionDimensions.includes(dimension) &&
        (performanceOnlyDimensions.some(excludedDimension =>
          value.some(selectedDimension => selectedDimension.value === excludedDimension),
        ) ||
          (metrics ?? []).some(metric => metric.value !== Metric.IP_CONVERSIONS))
      ) {
        return true;
      }
      if (
        performanceOnlyDimensions.includes(dimension) &&
        value.some(selectedDimension => attributionDimensions.includes(selectedDimension.value))
      ) {
        return true;
      }
      return false;
    },
    [metrics, checkShouldStopDisabledCheckEarly],
  );

  const getMetricDisabled = useCallback<GetOptionDisabled<IControlOption<string, Metric>>>(
    (option, value, checked) => {
      if (checked || checkShouldStopDisabledCheckEarly()) {
        return false;
      }
      const metric = option.value;

      const hasPacingSelected = value.some(selected => pacingMetrics.includes(selected.value));
      const hasPacingIncompatibleMetric = value.some(selected => pacingIncompatibleMetrics.includes(selected.value));
      const hasIncompatibleDimension = (dimensions ?? []).some(
        selected => !pacingCompatibleDimensions.includes(selected.value),
      );

      // Pacing metrics require Line Item / Campaign grain and no time bucket.
      if (pacingMetrics.includes(metric) && (!!timeBucket || hasPacingIncompatibleMetric || hasIncompatibleDimension)) {
        return true;
      }
      if (pacingIncompatibleMetrics.includes(metric) && hasPacingSelected) {
        return true;
      }

      // --- existing conversions/attribution gating (unchanged) ---
      if (
        metric === Metric.IP_CONVERSIONS &&
        performanceOnlyDimensions.some(excludedDimension =>
          (dimensions ?? []).some(selectedDimension => selectedDimension.value === excludedDimension),
        )
      ) {
        return true;
      }
      if (
        metric !== Metric.IP_CONVERSIONS &&
        (dimensions ?? []).some(selectedDimension => attributionDimensions.includes(selectedDimension.value))
      ) {
        return true;
      }
      return false;
    },
    [dimensions, timeBucket, checkShouldStopDisabledCheckEarly],
  );

  const pacingActive = useMemo(
    () => (metrics ?? []).some(selectedMetric => pacingMetrics.includes(selectedMetric.value)),
    [metrics],
  );

  useEffect(() => {
    if (pacingActive && timeBucket) {
      setValue('timeBucket', '');
    }
  }, [pacingActive, timeBucket, setValue]);

  const shouldShowEventFilter = useMemo<boolean>(() => {
    if (
      !showEventFilter ||
      (usePlatform && !platforms?.some(platform => platform.value === CustomReportChannel.Beeswax))
    ) {
      return false;
    }
    if (dimensions?.some(selectedDimension => attributionDimensions.includes(selectedDimension.value))) {
      return true;
    }
    return false;
  }, [showEventFilter, usePlatform, platforms, dimensions]);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Paper className='py-[24px] px-[30px] flex flex-col gap-y-[16px]'>
        <div className='flex items-center justify-between'>
          <div className='flex gap-[9px] items-center'>
            <span className='text-[24px] font-bold'>Filter</span>
            <FiltersBadge
              count={filterCount}
              sx={{ minWidth: '26px', width: 'unset', height: '26px', fontSize: '14px' }}
            />
            <ClearAllButton onClick={onReset} type='button' />
          </div>
          <div className='space-x-[9px]'>
            <Button variant='contained' startIcon={<SearchListIcon />} type='submit'>
              Generate Report
            </Button>
            <Button
              variant='outlined'
              startIcon={<SaveOutlined />}
              type='button'
              onClick={onSaveAsTemplateClick}
              disabled={apiActionsDisabled}
            >
              Save as Template
            </Button>
            <Button
              variant='outlined'
              startIcon={<TableRowsOutlined width='16' />}
              type='button'
              onClick={onScheduleReportClick}
              disabled={scheduleReportsDisabled}
            >
              Schedule Report
            </Button>
            <ScheduleReportTooltip />
          </div>
        </div>
        <div className='flex flex-wrap gap-[16px] items-center'>
          {usePlatform && (
            <div className='flex-[2] basis-[200px]'>
              <Controller
                control={control}
                name='platforms'
                rules={{
                  required: {
                    message: 'Select at least one platform',
                    value: true,
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className='flex-1'>
                    <ColumnSelect
                      {...field}
                      id='column-select-platforms'
                      options={PLATFORMS_OPTIONS}
                      startAdornment={<TvIcon />}
                      fullWidth
                      placeholder='Platform'
                      label='Platform'
                      error={!!error}
                    />
                    {error ? <FormErrorHelperText>{error.message}</FormErrorHelperText> : null}
                  </div>
                )}
              />
            </div>
          )}
          <div className='flex-[2] basis-[200px]'>
            <Controller
              control={control}
              name='dateRange'
              rules={{
                required: {
                  message: 'Date range is required.',
                  value: true,
                },
              }}
              render={({ field: { ...field }, fieldState: { error } }) => (
                <>
                  {error ? (
                    <FormErrorHelperText sx={{ position: 'absolute', top: '-25px', left: '14px' }}>
                      {error?.message}
                    </FormErrorHelperText>
                  ) : null}
                  <DateRangeWithShortcuts
                    {...field}
                    fullWidth
                    disableFuture
                    dateRangeFormat={dateRangeFormat}
                    onChange={onDateRangeChange}
                    shortcuts={getShortcutItemsInclusive()}
                  />
                </>
              )}
            />
          </div>
          <Controller
            control={control}
            name='timeBucket'
            render={({ field, fieldState: { error } }) => (
              <div className='flex-1 basis-[200px] relative'>
                {error ? (
                  <FormErrorHelperText sx={{ position: 'absolute', top: '-25px', left: '14px' }}>
                    {error.message}
                  </FormErrorHelperText>
                ) : null}
                <Select
                  {...field}
                  startAdornment={<AccessTime />}
                  displayEmpty
                  disabled={pacingActive}
                  renderValue={(value: TimeBucket) =>
                    !timeBucket ? (
                      <SelectInputPlaceholder>Group by Timeframe</SelectInputPlaceholder>
                    ) : (
                      <>{TIME_LABELS[value]}</>
                    )
                  }
                  error={!!error}
                  sx={{
                    [`& .${inputBaseClasses.input}`]: {
                      padding: '12px 16px 12px 8px',
                    },
                  }}
                  slotProps={{
                    paper: { className: 'mt-2' },
                  }}
                  fullWidth
                >
                  {Object.values(TimeBucket).map(value => (
                    <MenuItem key={value} value={value}>
                      {TIME_LABELS[value]}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}
          />
          <Controller
            control={control}
            name='template'
            render={({ field }) => (
              <Autocomplete
                {...field}
                id='template-select'
                sx={{ flex: 2, flexBasis: '200px' }}
                options={templates}
                fullWidth
                disabled={loadingClients || loadingTemplates}
                onChange={(_, data) => field.onChange(data)}
                getOptionLabel={option => option?.name || ''}
                disableClearable
                isOptionEqualToValue={(option, selectValue) => selectValue && option.id === selectValue?.id}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Template'
                    variant='outlined'
                    sx={{
                      [`& .${inputBaseClasses.root}.${inputBaseClasses.adornedEnd}`]: {
                        padding: '0 14px',
                      },
                      [`& .${inputBaseClasses.input}.${autocompleteClasses.input}`]: {
                        padding: '12px 16px 12px 8px',
                      },
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: <Web />,
                        endAdornment: <ArrowDropDown />,
                      },
                    }}
                  />
                )}
              />
            )}
          />
        </div>
        <div className='flex gap-[16px] items-center justify-between'>
          <Controller
            control={control}
            name='dimensions'
            disabled={omniPlatformDisabledCondition}
            rules={{
              validate: (_, formValues) =>
                (formValues.dimensions?.length ?? 0) + (formValues.metrics?.length ?? 0) > 0 ||
                'Select at least one dimension or metric',
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex-1'>
                <ColumnSelect
                  {...field}
                  id='column-select-dimensions'
                  options={dimensionOptions}
                  grouped
                  startAdornment={<TextFirstlineLeftIcon className='w-[19px]' />}
                  fullWidth
                  placeholder='Dimensions'
                  error={!!error}
                  getOptionDisabled={getDimensionDisabled}
                />
                {error ? <FormErrorHelperText>{error.message}</FormErrorHelperText> : null}
              </div>
            )}
          />
          <Controller
            control={control}
            name='metrics'
            disabled={omniPlatformDisabledCondition}
            rules={{
              validate: (_, formValues) =>
                (formValues.dimensions?.length ?? 0) + (formValues.metrics?.length ?? 0) > 0 ||
                'Select at least one dimension or metric',
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex-1'>
                <ColumnSelect
                  {...field}
                  id='column-select-metrics'
                  options={metricOptions}
                  grouped={groupedMetrics}
                  startAdornment={<SearchListIconAlt />}
                  fullWidth
                  placeholder='Metrics'
                  label='Metric'
                  error={!!error}
                  getOptionDisabled={getMetricDisabled}
                />
                {error ? <FormErrorHelperText>{error.message}</FormErrorHelperText> : null}
              </div>
            )}
          />
        </div>
        <div className='flex gap-[6px] flex-wrap items-center justify-start'>
          <FilterPopover
            clients={clients}
            filters={{
              clients: filterClients,
              advertisers: filterAdvertisers,
              campaigns: filterCampaigns,
              events: filterEvents,
            }}
            campaigns={campaigns}
            events={events}
            loadingClients={loadingClients}
            loadingCampaigns={loadingCampaigns}
            loadingEvents={loadingEvents}
            loadCampaigns={loadCampaigns}
            loadEvents={loadEvents}
            disabled={omniPlatformDisabledCondition}
            groupedCampaigns={usePlatform}
            showEventFilter={shouldShowEventFilter}
          />
        </div>
      </Paper>
    </form>
  );
};

export default Filter;
